import { Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { cn } from "../lib/utils";
import { BadgeEtatCahier } from "./coloriage-accueil";
import { ActionsDuCahier, CaseDePage, useCasesDuCahier } from "./coloriage-actions";
import {
  affichageDeLecture,
  ErreurColoriage,
  FilColoriage,
  ImageVersion,
  IntrouvableColoriage,
  idValide,
  RelectureEchouee,
} from "./coloriage-commun";
import { DemandesDuCahier, type Retour } from "./coloriage-demandes";
import { BADGE_ROUGE, BADGE_VERT, LegendeJugements, PointsDesVersions } from "./coloriage-points";
import { useCahierColoriage } from "./coloriage-requetes";
import { EditeurDuBrouillon } from "./coloriage-sommaire";
import {
  type EtatDePage,
  etatDePage,
  formatLong,
  modeleCourt,
  poids,
  proportionsDuFormat,
  titrePage,
} from "./coloriage-textes";
import type { CahierDetail, CahierResume, PageResume } from "./contract";
import { compter } from "./format";
import { ConsoleShell } from "./shell";
import { Chargement } from "./states";

/**
 * Un cahier (`/coloriage/cahiers/$id`) : la grille des pages, un point par version, la
 * vignette de la version montree et ce qui reste a faire sur chaque page.
 *
 * Depuis le ticket 3 (#152), la grille lance aussi le travail du PC : une case par carte, la
 * barre d'actions et, au-dessus de la grille, les demandes qui tournent sur ce cahier, relues
 * toutes les 5 s tant qu'il y en a (`useCahierColoriage`). Elles vivent dans
 * `coloriage-actions.tsx` et `coloriage-demandes.tsx` ; ce fichier ne fait que les poser.
 *
 * Un brouillon (#271), ne dans la console, n'a ni version ni grille : la page montre l'editeur
 * de son sommaire et son lancement (`coloriage-sommaire.tsx`). Lance, il devient ouvert, la
 * page relue passe a la grille, et le message du lancement y prend le focus.
 */

export function ColoriageCahierPage({ id }: { id: string }) {
  const valide = idValide(id);
  const lecture = useCahierColoriage(id, valide);
  // Jamais l'ecran d'erreur et la grille ensemble (`affichageDeLecture`).
  const affichage = affichageDeLecture(lecture);
  const detail = affichage.contenu ? lecture.data : undefined;
  // Ce que le lancement d'un brouillon a pose : la grille qui le remplace le dit.
  const [lancement, setLancement] = useState<Retour | null>(null);

  return (
    <ConsoleShell connecte={affichage.contenu}>
      <FilColoriage>{detail?.cahier.titre}</FilColoriage>
      {!valide ? <IntrouvableColoriage quoi={`le cahier ${id}`} /> : null}
      {valide && lecture.isPending ? <Chargement quoi="du cahier" /> : null}
      {affichage.ecranDErreur ? (
        <ErreurColoriage error={lecture.error} quoi={`le cahier ${id}`} />
      ) : null}
      {affichage.bandeau ? <RelectureEchouee error={lecture.error} /> : null}
      {detail === undefined ? null : detail.cahier.etat === "brouillon" ? (
        <EditeurDuBrouillon apresLancement={setLancement} cahier={detail.cahier} />
      ) : (
        <Grille detail={detail} lancement={lancement} />
      )}
    </ConsoleShell>
  );
}

/** Les pages toutes jugees dont aucune version n'est bonne : celles a relancer. */
export function pagesSansBonne(pages: readonly PageResume[]): number {
  return pages.filter((page) => etatDePage(page).ton === "sans_bonne").length;
}

function Grille({ detail, lancement }: { detail: CahierDetail; lancement: Retour | null }) {
  const { cahier, pages, demandes } = detail;
  const cases = useCasesDuCahier(cahier, pages, demandes);
  const [retour, setRetour] = useState<Retour | null>(lancement);

  // Le lancement d'un brouillon peut finir apres que la grille l'a remplace (la relecture du
  // cahier arrive parfois avant la fin de l'ecriture) : il est pose des qu'il arrive.
  useEffect(() => {
    if (lancement !== null) setRetour(lancement);
  }, [lancement]);
  const clos = cahier.etat === "clos";
  const aJuger = pages.reduce((total, page) => total + page.a_juger, 0);
  const sousTitre = [
    cahier.sous_titre,
    modeleCourt(cahier.modele),
    formatLong(cahier.format),
    compter(cahier.versions, "version", "versions"),
  ]
    .filter((morceau) => morceau !== null)
    .join(" · ");

  return (
    <>
      <header className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3">
        <div className="min-w-0">
          <h1 className="flex flex-wrap items-center gap-3 text-2xl font-semibold">
            {cahier.titre}
            {cahier.etat === "clos" ? <BadgeEtatCahier etat="clos" /> : null}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">{sousTitre}</p>
        </div>
        <ul aria-label="Où en est le cahier" className="flex flex-wrap gap-2 text-sm">
          <Puce>{compter(cahier.choisies, "page choisie", "pages choisies")}</Puce>
          <Puce>{`${pagesSansBonne(pages)} sans version bonne`}</Puce>
          <Puce>{compter(aJuger, "version à juger", "versions à juger")}</Puce>
          <Puce>{`images : ${poids(cahier.octets_images)}`}</Puce>
        </ul>
      </header>

      <ActionsDuCahier
        annoncer={setRetour}
        cahier={cahier}
        cochees={cases.cochees}
        demandes={demandes}
        retour={retour}
        viderLaSelection={cases.vider}
      />

      <DemandesDuCahier annoncer={setRetour} demandes={demandes} maintenant={Date.now()} />

      <LegendeJugements>
        {clos
          ? "un point par version · un clic sur une page ouvre son jugement"
          : "un point par version · les pages sans version bonne sont cochées d'office · un clic sur une page ouvre son jugement"}
      </LegendeJugements>

      {pages.length === 0 ? (
        <p className="text-sm text-muted-foreground">Ce cahier n'a encore aucune page.</p>
      ) : (
        // 6 colonnes sur grand ecran, 2 sur telephone. `min-w-0` : une vignette ne doit jamais
        // elargir sa colonne au-dela de sa part. `relative` : la case se pose sur la carte.
        <ul className="grid grid-cols-2 gap-3.5 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
          {pages.map((page) => (
            <li className="relative min-w-0" key={page.id}>
              <CaseDePage
                basculer={() => cases.basculer(page.id)}
                cochable={!clos}
                cochee={cases.cochees.includes(page.id)}
                titre={titrePage(page.rang, page.titre)}
              />
              <CartePage
                cochee={cases.cochees.includes(page.id)}
                format={cahier.format}
                page={page}
              />
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

function Puce({ children }: { children: string }) {
  return <li className="rounded-full bg-muted px-3 py-1">{children}</li>;
}

/**
 * La carte d'une page. Toute la carte est le lien : son nom dit la page et son etat, les
 * points gardent chacun leur infobulle (`version 2 : Défaut`). Sa case (`CaseDePage`) est
 * posee a cote, par-dessus son coin gauche ; cochee, la carte prend un contour plein.
 */
function CartePage({
  page,
  format,
  cochee,
}: {
  page: PageResume;
  format: CahierResume["format"];
  cochee: boolean;
}) {
  const etat = etatDePage(page);
  const titre = titrePage(page.rang, page.titre);
  const proportions = proportionsDuFormat(format);
  // La vignette est toujours une des versions de la page : son point porte son empreinte.
  const montree = page.versions.find((version) => version.id === page.montree);
  const nom = [titre, etat.texte, "preChoix" in etat ? etat.preChoix : null]
    .filter((morceau) => morceau !== null)
    .join(", ");

  return (
    <Link
      aria-label={nom}
      className={cn(
        "flex h-full flex-col gap-2 rounded-lg border bg-card p-2.5 text-card-foreground transition-colors hover:border-foreground/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        cochee ? "border-foreground ring-1 ring-foreground" : "border-border",
      )}
      params={{ id: String(page.id) }}
      to="/coloriage/pages/$id"
    >
      {/* A la hauteur de la case posee dans le coin gauche : les points restent sur sa ligne. */}
      <span className="flex min-h-7 items-center justify-end">
        <PointsDesVersions versions={page.versions} />
      </span>
      {montree === undefined ? (
        <span
          className="flex w-full items-center justify-center rounded-md border border-dashed border-border bg-muted text-xs text-muted-foreground"
          style={{ aspectRatio: `${proportions.largeur} / ${proportions.hauteur}` }}
        >
          aucune image
        </span>
      ) : (
        // La grille ne connait pas les dimensions des versions : le format du cahier les
        // donne. `object-contain` : une version d'une autre proportion n'est pas deformee.
        <ImageVersion
          alt=""
          className="h-auto w-full object-contain"
          empreinte={montree.empreinte}
          hauteur={proportions.hauteur}
          key={montree.id}
          largeur={proportions.largeur}
          paresseuse
          versionId={montree.id}
        />
      )}
      <span className="text-sm font-semibold leading-snug">{titre}</span>
      <EtatCarte etat={etat} />
    </Link>
  );
}

const TON_BADGE: Readonly<Record<EtatDePage["ton"], string>> = {
  choisie: BADGE_VERT,
  sans_bonne: BADGE_ROUGE,
  a_juger: "bg-foreground text-background",
  a_choisir: "bg-muted text-muted-foreground",
  vide: "bg-muted text-muted-foreground",
};

function EtatCarte({ etat }: { etat: EtatDePage }) {
  return (
    <span className="mt-auto flex flex-wrap items-center gap-x-1.5 gap-y-1">
      <span
        className={cn(
          "inline-flex whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-semibold",
          TON_BADGE[etat.ton],
        )}
      >
        {etat.texte}
      </span>
      {"preChoix" in etat && etat.preChoix !== null ? (
        <span className="text-xs text-muted-foreground">{etat.preChoix}</span>
      ) : null}
    </span>
  );
}
